import { useRef } from 'react';

interface SwipeHandlers {
  onSwipedLeft?: () => void;
  onSwipedRight?: () => void;
  onSwipedDown?: () => void;
}

interface SwipeOptions {
  delta?: number;
}

interface TouchPoint {
  x: number;
  y: number;
}

export function useSwipe(handlers: SwipeHandlers, options: SwipeOptions = {}) {
  const { delta = 50 } = options;
  const startPointRef = useRef<TouchPoint | null>(null);
  const isTrackingRef = useRef(false);

  const onTouchStart = (event: React.TouchEvent) => {
    if (event.touches.length !== 1) {
      startPointRef.current = null;
      isTrackingRef.current = false;
      return;
    }

    const touch = event.touches[0];
    if (!touch) {
      startPointRef.current = null;
      isTrackingRef.current = false;
      return;
    }

    startPointRef.current = { x: touch.clientX, y: touch.clientY };
    isTrackingRef.current = true;
  };

  const onTouchMove = (event: React.TouchEvent) => {
    if (!isTrackingRef.current || event.touches.length !== 1) {
      return;
    }

    event.preventDefault();
  };

  const onTouchEnd = (event: React.TouchEvent) => {
    const startPoint = startPointRef.current;
    const touch = event.changedTouches[0];

    startPointRef.current = null;
    const wasTracking = isTrackingRef.current;
    isTrackingRef.current = false;

    if (!wasTracking || !startPoint || !touch) {
      return;
    }

    const deltaX = touch.clientX - startPoint.x;
    const deltaY = touch.clientY - startPoint.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (absX < delta && absY < delta) {
      return;
    }

    if (absX > absY) {
      if (deltaX < 0) {
        handlers.onSwipedLeft?.();
      } else {
        handlers.onSwipedRight?.();
      }
      return;
    }

    if (deltaY > 0) {
      handlers.onSwipedDown?.();
    }
  };

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  };
}
